/**
 * F 429 — the passphrase + anchor modal for sealing (or opening) one block.
 *
 * WHY THIS IS ITS OWN FILE (measured, not tidiness)
 * -------------------------------------------------
 * `RichDragHandleMenu` is the only file in the app that mixes BlockNote's
 * `Components.Generic.Menu.*` with Mantine components. BlockNote's menu calls
 * `assertEmpty` on the props it receives, so a Mantine-only prop handed to a
 * `Menu.Item` throws in development — and F 371 shipped a test that forbids
 * `leftSection=` and `data-testid=` ANYWHERE in that file, because the test
 * cannot tell which library a given JSX element belongs to.
 *
 * That invariant is correct and worth keeping: it is what stops the next
 * person handing `data-testid` to a `Menu.Item` and getting a runtime throw
 * that only reproduces in dev builds.
 *
 * So the modal — which is entirely Mantine, and is not a menu concern at all —
 * lives here instead, and the menu file keeps only the one `onClick` that
 * opens it. A menu item that opens a dialog does not have to render the
 * dialog.
 */
import React from 'react';
import { Modal, Stack, TextInput, Button, Text, Badge, Group, ActionIcon } from '@mantine/core';
import { IconTag, IconPencil, IconCheck, IconX } from '@tabler/icons-react';
import './EncryptedBlock.css';

export interface BlockSealModalProps {
  opened: boolean;
  /** True ⇒ the block is sealed and this modal opens it. False ⇒ seals it. */
  opening: boolean;
  passphrase: string;
  anchor: string;
  error: string | null;
  busy: boolean;
  onPassphraseChange: (value: string) => void;
  onAnchorChange: (value: string) => void;
  onSubmit: () => void;
  onClose: () => void;
  /**
   * F 487 — the tags already on a SEALED block.
   *
   * The operator's flow is: seal a block, THEN see the tag the content
   * produced, and adjust it. So the tag is not only an input on the way
   * in — it is something the operator reads and edits afterwards.
   *
   * Empty when the block is not sealed yet (nothing to adjust), and empty
   * when the block is not sealed at all.
   */
  existingAnchors?: Array<{ id: string; text: string; kind?: string; source?: string }>;
  /**
   * F 487 — save an adjusted tag. Called with the new tag list.
   */
  onAnchorsChange?: (anchors: Array<{ id: string; text: string; kind?: string; source?: string }>) => void;
}

export const BlockSealModal: React.FC<BlockSealModalProps> = ({
  opened,
  opening,
  passphrase,
  anchor,
  error,
  busy,
  onPassphraseChange,
  onAnchorChange,
  onSubmit,
  onClose,
  existingAnchors,
  onAnchorsChange,
}) => {
  // F 487 — the tag the operator is adjusting. Local state, because the
  // edit is not committed until they save it.
  const [editing, setEditing] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState('');

  React.useEffect(() => {
    if (!opened) {
      setEditing(null);
      setDraft('');
    }
  }, [opened]);

  const tags = existingAnchors ?? [];

  const startEdit = (id: string, text: string) => {
    setEditing(id);
    setDraft(text);
  };

  const commit = (id: string) => {
    const next = tags.map((t) => (t.id === id ? { ...t, text: draft.trim() } : t));
    onAnchorsChange?.(next);
    setEditing(null);
    setDraft('');
  };

  return (
  <Modal
    opened={opened}
    onClose={onClose}
    title={opening ? 'Decrypt block' : 'Seal this block'}
    data-testid="fc-block-encrypt-modal"
    centered
  >
    <Stack gap="sm">
      {opening ? (
        <Text size="sm" c="dimmed">
          Enter the passphrase. The plaintext will replace the sealed stub.
        </Text>
      ) : (
        <>
          <Text size="sm" c="dimmed">
            Pick a passphrase. Then write a short public anchor — a hint the
            assistant uses to <em>find</em> this block without reading it.
          </Text>
          <Text size="xs" c="dimmed">
            Example: &ldquo;weekly-fee&rdquo;, &ldquo;backup-codes-2024&rdquo;, &ldquo;team-photos-q3&rdquo;.
          </Text>
        </>
      )}

      {/* F 487 — THE TAG THE OPERATOR CAN SEE AND ADJUST.
          A tag is plaintext by design: it is how a reader without the key
          finds the block. So it is safe to show, and it is the operator's
          to edit. */}
      {tags.length > 0 ? (
        <Stack gap={6} data-testid="fc-block-encrypt-tags">
          <Group gap={6}>
            <IconTag size={14} aria-hidden="true" />
            <Text size="sm" fw={500}>Search tags</Text>
            <Text size="xs" c="dimmed">{tags.length}</Text>
          </Group>
          {tags.map((t) => (
            <Group key={t.id} gap={6} wrap="nowrap">
              {editing === t.id ? (
                <>
                  <TextInput
                    size="xs"
                    value={draft}
                    onChange={(e) => setDraft(e.currentTarget.value)}
                    data-testid="fc-block-encrypt-tag-draft"
                    autoFocus
                    style={{ flex: 1 }}
                  />
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    onClick={() => commit(t.id)}
                    data-testid="fc-block-encrypt-tag-save"
                    aria-label="Save the tag"
                  >
                    <IconCheck size={14} />
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    onClick={() => { setEditing(null); setDraft(''); }}
                    aria-label="Cancel"
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </>
              ) : (
                <>
                  <Badge
                    variant="light"
                    data-testid="fc-block-encrypt-tag"
                    data-source={t.source ?? 'user'}
                    style={{ flex: 1, textTransform: 'none' }}
                  >
                    {t.text}
                  </Badge>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    onClick={() => startEdit(t.id, t.text)}
                    data-testid="fc-block-encrypt-tag-edit"
                    aria-label="Adjust the tag"
                  >
                    <IconPencil size={14} />
                  </ActionIcon>
                </>
              )}
            </Group>
          ))}
        </Stack>
      ) : null}

      <TextInput
        label="Anchor (public hint)"
        placeholder={opening ? '' : 'e.g. backup-codes-2024'}
        value={anchor}
        onChange={(e) => onAnchorChange(e.currentTarget.value)}
        disabled={opening}
        data-testid="fc-block-encrypt-anchor"
        maxLength={120}
      />

      <TextInput
        label="Passphrase"
        type="password"
        placeholder="Passphrase"
        value={passphrase}
        onChange={(e) => onPassphraseChange(e.currentTarget.value)}
        data-testid="fc-block-encrypt-passphrase"
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); onSubmit(); }
        }}
      />

      {error ? (
        <Text size="sm" role="alert" data-testid="fc-block-encrypt-error">
          {error}
        </Text>
      ) : null}

      <Button
        onClick={onSubmit}
        loading={busy}
        data-testid="fc-block-encrypt-submit"
      >
        {opening ? 'Decrypt' : 'Encrypt'}
      </Button>
    </Stack>
  </Modal>
  );
};

export default BlockSealModal;